import { NSARouterMakerResult } from "namirasoft-account-react";
import { NamirasoftAPIProductMetaDatabase, ProductFullRow } from "namirasoft-api-product";
import { FilterItem, FilterItemOperator } from "namirasoft-core";
import { NSBox, NSBoxEntity, NSBoxString, NSBoxTextArea, NSColumn, NSRow } from "namirasoft-site-react";
import { WorkspaceInputRow, WorkspaceRow } from "namirasoft-workspace";
import { useRef } from "react";
import { WorkspaceEntities } from "../WorkspaceEntities";
import { WorkspaceLayout } from "../WorkspaceLayout";
import { WorkspaceProps } from "../WorkspaceProps";

export interface IWorkspaceEditPageProps extends NSARouterMakerResult, WorkspaceProps
{
    isEdit: boolean
}

// product_ids is optional, it is null when the workspace belongs to all the products
function getProductIDs(value: any): string[]
{
    if (Array.isArray(value))
        return value;
    if (typeof value == "string" && value.trim())
        return JSON.parse(value);
    return [];
}

function WorkspaceEditPage(props: IWorkspaceEditPageProps)
{
    const NSBoxString_Name = useRef<NSBoxString>(null);
    const NSBoxEntity_ProductIDs = useRef<NSBoxEntity<ProductFullRow>>(null);
    const NSBoxTextArea_Description = useRef<NSBoxTextArea>(null);

    return (
        <WorkspaceLayout<WorkspaceRow, WorkspaceInputRow>
            action={{
                description: `${!props.isEdit ? "Add a new workspace, with its details, to your list." : ""}`,
            }}
            {...props}
            pages={{
                entity: WorkspaceEntities.workspace(props, () => window.location.reload()),
                edit: {
                    ...props,
                    ui: {
                        tab_page_edit: {
                            editor: {
                                getInputRow: () =>
                                {
                                    let name: string = NSBoxString_Name.current?.getValue() ?? "";
                                    let ids: string[] = NSBoxEntity_ProductIDs.current?.getValueAll() ?? [];
                                    let product_ids: string[] | null = ids.length > 0 ? ids : null;
                                    let description: string = NSBoxTextArea_Description.current?.getValue() ?? "";

                                    return {
                                        name,
                                        product_ids,
                                        description,
                                        workspace_category: [],
                                        workspace_field: [],
                                        workspace_tag: []
                                    };
                                },
                                setRow: (row) =>
                                {
                                    NSBoxString_Name.current?.setValue(row.name);
                                    NSBoxEntity_ProductIDs.current?.setValueAll(getProductIDs(row.product_ids));
                                    NSBoxTextArea_Description.current?.setValue(row.description ?? "");
                                }
                            }
                        }
                    }
                }
            }}
        >
            <NSColumn>
                <NSRow>
                    <NSBoxString
                        ref={NSBoxString_Name}
                        title="Name"
                        required
                        info={{ link: "https://namirasoft.com/workspace/console-guide/workspace/#field-name" }}
                    />
                    <NSBoxEntity<ProductFullRow>
                        title="Product"
                        ref={NSBoxEntity_ProductIDs}
                        table_name="product"
                        list={async (filters, page, size, sorts, _all_workspaces) =>
                        {
                            let meta = new NamirasoftAPIProductMetaDatabase();
                            let filter_namirasoft = new FilterItem(meta.tables.product, meta.tables.product.columns.namirasoft, false, FilterItemOperator.all.equals, "1")
                            let filter_workspace = new FilterItem(meta.tables.product, meta.tables.product.columns.namirasoft_workspace, false, FilterItemOperator.all.equals, "1")
                            return await props.servers.product.product.List([...(filters ?? []), filter_namirasoft, filter_workspace], page, size, sorts);
                        }}
                        required={false}
                        multiple
                        getTitle={(item) => item.name}
                        getValue={(item) => item.id}
                        getSort={() => null}
                        info={{ link: "https://namirasoft.com/workspace/console-guide/workspace/#field-product" }}
                    />
                </NSRow>
                <NSBoxTextArea
                    ref={NSBoxTextArea_Description}
                    title="Description"
                    required={false}
                    style={{ width: NSBox.width.double }}
                    info={{ link: "https://namirasoft.com/workspace/console-guide/workspace/#field-description" }}
                />
            </NSColumn>
        </WorkspaceLayout >
    );
}

export default WorkspaceEditPage;